import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { updateTourStatus } from '@/store/actions/userAction';
import apiConfig from '@/config/apiConfig';
import { apiBaseURL, Tokens } from '@/constants';
import { getSecureItem } from '@/lib/secureStorage';

const TOUR_STEPS = [
  // 0 — Welcome
  {
    popover: {
      title: 'Welcome to Acclimetry!',
      description:
        "Let's take a quick tour to help you get started. Use the buttons to navigate, or press Escape to exit at any time.",
      side: 'over',
      align: 'center',
    },
  },
  // 1 — Portfolios
  {
    element: '#tour-nav-portfolios',
    popover: {
      title: 'Portfolios',
      description:
        'Your main dashboard. View all portfolios in a table. Click any portfolio row to open it, view its assets, run buy/sell transactions, and review allocation details.',
      side: 'right',
      align: 'start',
    },
  },
  // 2 — Allocations
  {
    element: '#tour-nav-allocations',
    popover: {
      title: 'Allocations',
      description:
        'Create and manage allocation strategies for your portfolios and portfolio groups. Set target weights, monitor drift, and review performance vs. targets.',
      side: 'right',
      align: 'start',
    },
  },
  // 3 — Analysis
  {
    element: '#tour-nav-analysis',
    popover: {
      title: 'Analysis',
      description:
        'See how your portfolios are invested — by asset class, currency, region, country and issuer — for one book, a group or a family entity, what is unrealised against book cost, and how it has changed since an earlier date.',
      side: 'right',
      align: 'start',
    },
  },
  // 4 — Tolerance Manager
  {
    element: '#tour-nav-tolerance-manager',
    popover: {
      title: 'Tolerance Manager',
      description:
        'Set tolerance thresholds on your custom fields for portfolios. Monitor when allocations breach the defined upper/lower bounds and receive alerts automatically.',
      side: 'right',
      align: 'start',
    },
  },
  // 5 — Clients
  {
    element: '#tour-nav-clients',
    popover: {
      title: 'Clients',
      description:
        'Add and manage your investment clients. Associate clients with portfolios for personalised reporting, onboarding, and oversight.',
      side: 'right',
      align: 'start',
    },
  },
  // 6 — Data section intro (Next expands the section)
  {
    element: '#tour-nav-data',
    popover: {
      title: 'Data',
      description:
        'Your investment data hub. Click <strong>Next</strong> to explore each sub-section.',
      side: 'right',
      align: 'start',
    },
  },
  // 7 — Data > Portfolio
  {
    element: '#tour-nav-data-portfolio',
    popover: {
      title: 'Data → Portfolio',
      description:
        'Create and manage your portfolios here. Click <strong>"+ Add Portfolio"</strong> to get started.',
      side: 'right',
      align: 'start',
    },
  },
  // 8 — Data > Portfolio-Groups
  {
    element: '#tour-nav-data-portfolio-groups',
    popover: {
      title: 'Data → Portfolio Groups',
      description:
        'Organise related portfolios into groups for consolidated reporting and group-level allocations.',
      side: 'right',
      align: 'start',
    },
  },
  // 9 — Data > Assets
  {
    element: '#tour-nav-data-assets',
    popover: {
      title: 'Data → Assets',
      description:
        'Add financial instruments to the platform. After adding, select assets and click <strong>"Authorise Asset"</strong> to enable them for buy/sell transactions.',
      side: 'right',
      align: 'start',
    },
  },
  // 10 — Data > Issuers
  {
    element: '#tour-nav-data-issuers',
    popover: {
      title: 'Data → Issuers',
      description:
        'Add the entities — companies, governments, institutions — that issue the financial instruments in your portfolios. Issuers can be linked to assets for richer analysis.',
      side: 'right',
      align: 'start',
    },
  },
  // 11 — Data > Prices
  {
    element: '#tour-nav-data-prices',
    popover: {
      title: 'Data → Prices',
      description:
        'View and manage asset price data. Accurate pricing feeds are essential for allocation drift calculations and performance reporting.',
      side: 'right',
      align: 'start',
    },
  },
  // 12 — Data > BYOD
  {
    element: '#tour-nav-data-byod',
    popover: {
      title: 'Data → BYOD',
      description:
        'Bring Your Own Data — create custom fields to extend assets and issuers with your own metrics. These fields are also used by the Tolerance Manager.',
      side: 'right',
      align: 'start',
    },
  },
  // 13 — Account section intro (Next expands the section)
  {
    element: '#tour-nav-account',
    popover: {
      title: 'Account',
      description:
        'Manage your personal and platform settings. Click <strong>Next</strong> to explore.',
      side: 'right',
      align: 'start',
    },
  },
  // 14 — Account > Profile
  {
    element: '#tour-nav-account-profile',
    popover: {
      title: 'Account → Profile',
      description:
        'View and update your personal information and change your password.',
      side: 'right',
      align: 'start',
    },
  },
  // 15 — Account > Setting
  {
    element: '#tour-nav-account-setting',
    popover: {
      title: 'Account → Settings',
      description:
        'Configure asset classes, sub-classes, types, sub-types, strategies, price hierarchies, portfolio data load settings, and third-party API keys.',
      side: 'right',
      align: 'start',
    },
  },
  // 16 — Done
  {
    popover: {
      title: "You're all set!",
      description:
        "That covers the key features of Acclimetry. Start by creating your first portfolio under <strong>Data → Portfolio</strong>. Good luck!",
      side: 'over',
      align: 'center',
    },
  },
];

const expandSection = (section) => {
  window.dispatchEvent(
    new CustomEvent('acclimetry:tour-expand', { detail: { section } })
  );
};

const useUserWalkthrough = () => {
  const dispatch = useDispatch();
  const driverRef = useRef(null);
  const hasChecked = useRef(false);

  const markTourComplete = useCallback(() => {
    dispatch(updateTourStatus());
  }, [dispatch]);

  useEffect(() => {
    const token = getSecureItem(Tokens.ADMIN_TOKEN);
    const role = getSecureItem(Tokens.ADMIN_ROLE);

    if (!token || role !== 'user' || hasChecked.current) return;

    hasChecked.current = true;

    const checkAndStartTour = async () => {
      try {
        const response = await apiConfig.get(`${apiBaseURL.USER}/info`);
        const isTour = response?.data?.data?.isTour;

        if (isTour === false) {
          const driverObj = driver({
            showProgress: true,
            allowClose: true,
            overlayOpacity: 0.4,
            stagePadding: 8,
            nextBtnText: 'Next',
            prevBtnText: 'Previous',
            doneBtnText: 'Done',
            steps: TOUR_STEPS,
            // onNextClick overrides default Next — must call moveNext() manually
            onNextClick: () => {
              const idx = driverRef.current?.getActiveIndex();
              if (idx === 6) {
                // Expand Data sub-menu, wait for CSS transition (300ms), then advance
                expandSection('data');
                setTimeout(() => driverRef.current?.moveNext(), 350);
                return;
              }
              if (idx === 13) {
                // Expand Account sub-menu, wait for CSS transition, then advance
                expandSection('account');
                setTimeout(() => driverRef.current?.moveNext(), 350);
                return;
              }
              driverRef.current?.moveNext();
            },
            onDestroyed: () => {
              markTourComplete();
            },
          });

          driverRef.current = driverObj;

          setTimeout(() => {
            driverObj.drive();
          }, 800);
        }
      } catch {
        // API failure — don't block the user
      }
    };

    checkAndStartTour();

    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, [markTourComplete]);
};

export default useUserWalkthrough;
